/**
 * RBAC permission catalog + system roles + route-permission map.
 *
 * Pure module: NO runtime side effects, NO localStorage, NO axios.
 * Source of truth = backend migrations `1800000000002` (seed), `...0004` (M5
 * 4-role cutover), `...0006` (M6 ops grant), `...0007` (M7 page refinement),
 * `...0008` (feature splits) → 63 permissions, 4 system roles.
 * Effective perms arrive via `GET /api/admin/staff/me/permissions`
 * (exact match or `'*'` wildcard server-side; super_admin role resolves to
 * `*`; a role-less staff account resolves to `[]` — fail-closed in the
 * backend, so this module must not invent access).
 *
 * NOTE: `PERMISSIONS` + `SYSTEM_ROLES` are **test/display-only** — verified
 * no app code imports them (the role editor reads the live server registry via
 * `GET /api/admin/staff/permissions`). Only `ROUTE_PERMS` + `hasPerm` gate UI.
 *
 * Canonical shapes (kept for tests):
 * - `SYSTEM_ROLES` is an ARRAY of `{ id, name, permissions, readOnly }`.
 * - `hasPerm(granted, [])` (empty required) → true (public within authed area).
 * - Wildcard granted (`*`) supported. Backend checks exact names only.
 */
import type { PermissionEntry } from './adminTypes';

export type { PermissionEntry };

/**
 * Route gate shape. OUTER array = anyOf (any one grants access).
 * An INNER array = an AND group: every member must be granted.
 *
 *   ['catalog:read']                          → any of: catalog:read
 *   ['orders:read', 'orders:write']           → any of (unchanged, legacy)
 *   [['catalog:write', 'vendors:read']]       → the one option requires BOTH
 *
 * The AND group exists because a create/edit page needs its own *write* perm
 * **and** the *read* perm of the reference data it loads (vendor/product
 * pickers). Without it, adding a perm to the array would LOOSEN the gate, since
 * the outer array is anyOf. Mirrors the backend, which ANDs `@RequirePermissions`.
 */
export type RequiredPerms = Array<string | string[]>;

export interface SystemRole {
  id: string;
  name: string;
  permissions: string[];
  readOnly: boolean;
}

function entry(key: string, group: string, description: string): PermissionEntry {
  return { key, group, description };
}

export const PERMISSIONS: PermissionEntry[] = [
  // dashboard
  entry('dashboard:read', 'dashboard', 'View dashboard stats'),
  entry('dashboard:read_revenue', 'dashboard', 'View revenue figures'),
  entry('dashboard:write', 'dashboard', 'Update dashboard targets'),
  // users
  entry('users:read', 'users', 'View users list and details'),
  entry('users:write', 'users', 'Create and update users'),
  entry('users:write_self', 'users', 'Update own profile'),
  entry('users:delete', 'users', 'Delete users'),
  // staff
  entry('staff:read', 'staff', 'View staff users and roles'),
  entry('staff:manage', 'staff', 'Manage staff roles, assignment and disable'),
  // audit
  entry('audit:read', 'audit', 'View admin audit trail'),
  // vendors
  entry('vendors:read', 'vendors', 'View vendors'),
  entry('vendors:write', 'vendors', 'Create and update vendors'),
  entry('vendors:assign', 'vendors', 'Assign vendors to users'),
  entry('vendors:force_state', 'vendors', 'Force-open / force-close vendors'),
  entry('vendors:write_self', 'vendors', 'Update own vendor profile'),
  // orders
  entry('orders:read', 'orders', 'View orders'),
  entry('orders:write', 'orders', 'Update orders'),
  entry('orders:assign', 'orders', 'Assign drivers to orders'),
  entry('orders:export', 'orders', 'Export orders'),
  // deliveries + tracking
  entry('deliveries:read', 'deliveries', 'View deliveries'),
  entry('tracking:read', 'tracking', 'View live tracking'),
  entry('tracking:read_all', 'tracking', 'View all tracking boards'),
  entry('tracking:write_self', 'tracking', 'Update own tracking state'),
  entry('drivers:read_stats', 'drivers', 'View driver statistics'),
  entry('drivers:read_remittance', 'drivers', 'View driver remittance history'),
  // catalog
  entry('catalog:read', 'catalog', 'View catalog'),
  entry('catalog:write', 'catalog', 'Manage catalog'),
  // merchandising (backend: Jwt+Admin only — listed for display, not gated)
  entry('merchandising:read', 'merchandising', 'View merchandising'),
  entry('merchandising:write', 'merchandising', 'Manage merchandising'),
  // ratings
  entry('ratings:read', 'ratings', 'View ratings'),
  entry('ratings:moderate', 'ratings', 'Moderate ratings'),
  // wallets
  entry('wallets:read', 'wallets', 'View wallets and transactions'),
  entry('wallets:export', 'wallets', 'Export wallet transactions'),
  entry('wallets:adjust', 'wallets', 'Adjust wallet balances'),
  entry('wallets:transfer', 'wallets', 'Transfer wallet funds'),
  entry('wallets:transfer_self', 'wallets', 'Transfer own funds'),
  entry('wallets:freeze', 'wallets', 'Freeze / unfreeze wallets'),
  entry('wallets:verify', 'wallets', 'Verify wallet transactions'),
  entry('wallets:remit', 'wallets', 'Remit wallet funds'),
  // payouts
  entry('payouts:read', 'payouts', 'View payouts'),
  entry('payouts:approve', 'payouts', 'Approve or reject payouts (maker-checker)'),
  entry('payouts:execute', 'payouts', 'Execute (pay) approved payouts'),
  // payments
  entry('payments:read', 'payments', 'View payments'),
  entry('payments:refund', 'payments', 'Refund payments'),
  // promotions
  entry('promotions:read', 'promotions', 'View promotions and promo codes'),
  entry('promotions:write', 'promotions', 'Manage promotions and promo codes'),
  entry('promotions:approve', 'promotions', 'Approve promotions (lead flag)'),
  // broadcast
  entry('broadcast:send', 'broadcast', 'Send push broadcasts (lead flag)'),
  // fraud
  entry('fraud:read', 'fraud', 'View fraud / COD-ban state'),
  entry('fraud:manage_cod_ban', 'fraud', 'Manage COD bans and devices'),
  // feature-split domains (backend migration ...0008)
  entry('app-categories:read', 'app-categories', 'View app categories (main/sub)'),
  entry('app-categories:write', 'app-categories', 'Manage app categories (main/sub/reorder)'),
  entry('other-stores:read', 'other-stores', 'View other stores'),
  entry('other-stores:write', 'other-stores', 'Manage other stores'),
  entry('operational-areas:read', 'operational-areas', 'View operational areas'),
  entry('operational-areas:write', 'operational-areas', 'Manage operational areas'),
  entry('featured-vendors:read', 'featured-vendors', 'View featured vendors'),
  entry('featured-vendors:write', 'featured-vendors', 'Manage featured vendors'),
  entry('featured-products:read', 'featured-products', 'View featured products'),
  entry('featured-products:write', 'featured-products', 'Manage featured products'),
  entry('best-products:read', 'best-products', 'View best products'),
  entry('best-products:write', 'best-products', 'Manage best products'),
  // console entry
  entry('admin:access', 'admin', 'Access the admin console'),
];

export const ALL_PERM_KEYS: string[] = PERMISSIONS.map((p) => p.key);

// Bundles mirroring the backend migrations (M5 4-role cutover, M6 ops grant,
// M7 page-access refinement, M8 feature splits). NOTE: display/test-only —
// effective perms always arrive via GET /api/admin/staff/me/permissions.
//
// [P0b OPEN] The monitor array below still lists catalog:*/merchandising:*, which
// the BACKEND migrations say were REMOVED by M7 (there is no backend M8).
// Resolve against the live DB before editing this array or its test:
//   SELECT r.name, GROUP_CONCAT(p.name) FROM user_staff_roles usr
//     JOIN staff_roles r ON r.id=usr.role_id
//     JOIN staff_role_permissions srp ON srp.role_id=r.id
//     JOIN permissions p ON p.id=srp.permission_id
//    WHERE r.name IN ('monitor','finance','admin') GROUP BY r.name;
const MONITOR_PERMS: string[] = [
  'admin:access',
  'orders:read',
  'orders:assign',
  'orders:write',
  'tracking:read_all',
  'vendors:read',
  'vendors:write',
  'vendors:force_state',
  'catalog:read',
  'catalog:write',
  'users:read',
  'merchandising:read',
  'merchandising:write',
  'payments:read',
  'payments:refund',
];

// M7 removed catalog:read from finance (hides App categories; Categories /
// Items side effect via shared perm). vendors:read kept so Stores stays.
const NEW_FINANCE_PERMS: string[] = [
  'admin:access',
  'orders:read',
  'orders:export',
  'tracking:read_all',
  'vendors:read',
  'users:read',
  'drivers:read_remittance',
  'wallets:read',
  'wallets:adjust',
  'payouts:read',
  'payouts:approve',
  'ratings:read',
];

const ADMIN_PERMS: string[] = [
  'admin:access',
  'users:read', 'users:write', 'users:write_self', 'users:delete',
  'staff:read', 'staff:manage',
  'audit:read',
  'vendors:read', 'vendors:write', 'vendors:assign', 'vendors:force_state', 'vendors:write_self',
  'orders:read', 'orders:write', 'orders:assign', 'orders:export',
  'deliveries:read',
  'tracking:read', 'tracking:read_all', 'tracking:write_self',
  'drivers:read_stats', 'drivers:read_remittance',
  'catalog:read', 'catalog:write',
  'merchandising:read', 'merchandising:write',
  'ratings:read', 'ratings:moderate',
  'payouts:read', 'payouts:approve', 'payouts:execute',
  'payments:read', 'payments:refund',
  'broadcast:send',
  'fraud:read', 'fraud:manage_cod_ban',
  // feature splits granted to admin by migration ...0008
  'app-categories:read', 'app-categories:write',
  'other-stores:read', 'other-stores:write',
  'operational-areas:read', 'operational-areas:write',
  'featured-vendors:read', 'featured-vendors:write',
  'featured-products:read', 'featured-products:write',
  'best-products:read', 'best-products:write',
];

export const SYSTEM_ROLES: SystemRole[] = [
  { id: 'super_admin', name: 'Super Admin', permissions: [...ALL_PERM_KEYS], readOnly: true },
  { id: 'monitor', name: 'Monitor', permissions: MONITOR_PERMS, readOnly: true },
  { id: 'finance', name: 'Finance', permissions: NEW_FINANCE_PERMS, readOnly: true },
  { id: 'admin', name: 'Admin', permissions: ADMIN_PERMS, readOnly: true },
];

/**
 * Minimum permission(s) required per admin route.
 * Guards redirect to `/admin/no-access` when none of the listed
 * permissions is granted (generic message, no enumeration).
 *
 * Semantics: **anyOf** (OR) — keep each value single-valued, because the
 * backend's `PermissionsGuard` ANDs multiple perms while this map ORs them.
 * Values must match the backend 1:1 (migrations `...0004`/`...0006`/`...0007`/
 * `...0008`); the backend is the source of truth.
 * Empty array = public within the authenticated area (supported; no route
 * currently relies on it).
 *
 * Param routes (`:id`) are used by App.tsx lookups only — never add them to
 * `REDIRECT_CANDIDATES` in route.utils.ts.
 */
export const ROUTE_PERMS: Record<string, RequiredPerms> = {
  '/admin': ['dashboard:read'],
  '/admin/dashboard': ['dashboard:read'],
  '/admin/staff': ['staff:read'],
  '/admin/roles': ['staff:read'],
  '/admin/audit-logs': ['audit:read'],
  '/admin/payouts': ['payouts:read'],
  '/admin/orders': ['orders:read'],
  '/admin/live-orders': ['orders:read'],
  '/admin/wallet-transactions': ['wallets:read'],
  '/admin/store-payouts': ['payouts:read'],
  '/admin/users': ['users:read'],
  '/admin/users/create': ['users:write'],
  '/admin/users/:id/edit': ['users:write'],
  '/admin/stores': ['vendors:read'],
  // create/edit need the vendor picker (`vendors:read`) plus the zone list
  // (`operational-areas:read`, loaded from the staff twin) in addition to write
  '/admin/stores/create': [
    ['vendors:write', 'vendors:read', 'operational-areas:read'],
  ],
  '/admin/stores/:id/edit': [
    ['vendors:write', 'vendors:read', 'operational-areas:read'],
  ],
  // Delivery Collection page: gated on drivers:read_remittance (not
  // users:read) so monitor loses the whole Delivery Collection sidebar
  // group (both children). finance + admin hold remittance → unaffected.
  // Backend driver APIs are log-only (known gap) — this is UI-level hiding.
  // Delivery Collection lists drivers via /admin/users/drivers (users:read)
  '/admin/drivers': [['drivers:read_remittance', 'users:read']],
  '/admin/items': ['catalog:read'],
  '/admin/items/create': [['catalog:write', 'catalog:read', 'vendors:read']],
  '/admin/items/:id/edit': [['catalog:write', 'catalog:read', 'vendors:read']],
  '/admin/items/reorder': [['catalog:write', 'catalog:read', 'vendors:read']],
  '/admin/ratings': ['ratings:read'],
  // Push composer loads the product picker (catalog:read)
  '/admin/push-notifications': [['broadcast:send', 'catalog:read']],
  '/admin/eagle-view': ['tracking:read_all'],
  '/admin/transactions': ['wallets:read'],
  '/admin/reports': ['dashboard:read_revenue'],
  '/admin/settings': ['users:write_self'],
  // ── Feature-split pages — 1:1 with backend migration ...0008 ─────────────
  // No more `vendors:assign` / `dashboard:read` stand-ins: each page names the
  // exact perm its backend twin requires, so the role editor can express every
  // sidebar distinction directly.
  '/admin/operational-areas': ['operational-areas:read'],
  // create loads the zone list for reference → needs the read perm too
  '/admin/operational-areas/create': [
    ['operational-areas:write', 'operational-areas:read'],
  ],
  '/admin/operational-areas/:id/edit': ['operational-areas:write'],
  '/admin/operational-areas/:id/assign-vendors': [['vendors:assign', 'vendors:read']],
  // App Categories manages vendor assignments → needs the vendor picker
  '/admin/app-categories': [['app-categories:read', 'vendors:read']],
  '/admin/featured-vendors': ['featured-vendors:read'],
  '/admin/featured-vendors/add': [['featured-vendors:write', 'vendors:read']],
  '/admin/featured-vendors/:id/edit': [['featured-vendors:write', 'vendors:read']],
  '/admin/featured-products': ['featured-products:read'],
  '/admin/featured-products/create': [['featured-products:write', 'catalog:read']],
  '/admin/featured-products/:id/edit': [['featured-products:write', 'catalog:read']],
  '/admin/best-products': ['best-products:read'],
  '/admin/best-products/create': [['best-products:write', 'catalog:read']],
  '/admin/best-products/:id/edit': [['best-products:write', 'catalog:read']],
  '/admin/other-stores': ['other-stores:read'],
  '/admin/other-stores/add': [['other-stores:write', 'vendors:read']],
  '/admin/other-stores/:id/edit': [['other-stores:write', 'vendors:read']],
  '/admin/mobile': ['merchandising:read'],
  '/admin/cod-bans': ['fraud:read'],
  '/admin/promo-codes': ['promotions:read'],
  // create/edit load the promotions list (promotions:read) for their picker
  '/admin/promo-codes/create': [['promotions:write', 'promotions:read']],
  '/admin/promo-codes/:id/edit': [['promotions:write', 'promotions:read']],
  '/admin/store-balance': ['payouts:read'],
  // Aligned with the Sidebar (was users:read, which monitor holds).
  // Monitor lacks drivers:read_remittance → hidden; finance/admin keep it.
  '/admin/collections-log': ['drivers:read_remittance'],
  '/admin/categories': ['catalog:read'],
  '/admin/categories/create': [['catalog:write', 'catalog:read', 'vendors:read']],
  '/admin/categories/:id/edit': [['catalog:write', 'catalog:read', 'vendors:read']],
  '/admin/delivery-fee-promotions': ['promotions:read'],
  '/admin/delivery-fee-promotions/create': [
    ['promotions:write', 'promotions:read', 'vendors:read'],
  ],
  '/admin/delivery-fee-promotions/:id/edit': [
    ['promotions:write', 'promotions:read', 'vendors:read'],
  ],
  // store sub-tabs hit store-scoped endpoints (vendors:read) and product data
  '/admin/store-items': [['vendors:read', 'catalog:read']],
  '/admin/store-ratings': ['ratings:read'],
  '/admin/store-orders': [['orders:read', 'vendors:read']],
};

/**
 * True when the effective permission set carries the backend wildcard `'*'`.
 *
 * `RbacService` short-circuits `staff_roles.name = 'super_admin'` to `'*'`,
 * so this is the client-side equivalent of "is super admin" — use it to gate
 * actions the backend restricts to super_admin (e.g. broadcast-to-ALL), not
 * for ordinary permission checks (those go through `hasPerm`).
 */
export function isSuperAdmin(granted: string[]): boolean {
  return granted.includes('*');
}

/**
 * Pure permission check: true when ANY of the required permissions
 * is present in the granted set. Empty `required` means public
 * (within the authenticated area). Supports global `'*'` in the
 * granted set (backend wildcard semantics: exact match or `*`).
 */
export function hasPerm(
  granted: string[],
  required: string | RequiredPerms,
): boolean {
  const needed: RequiredPerms = Array.isArray(required) ? required : [required];
  if (needed.length === 0) return true;
  const wildcard = granted.includes('*');
  return needed.some((entry) => {
    // AND group: every member must be present
    if (Array.isArray(entry)) {
      return wildcard || entry.every((p) => granted.includes(p));
    }
    if (typeof entry !== 'string') return false;
    return wildcard || granted.includes(entry);
  });
}
